import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isDemo } from '@/lib/demo';

const PAD = 6;

export const DemoTour = ({ role, steps, activeTab, setActiveTab }) => {
  const { t } = useTranslation();
  const key = `rs_demo_tour_done_${role}`;
  const [open, setOpen] = useState(() => isDemo() && localStorage.getItem(key) !== '1');
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];

  useEffect(() => {
    if (open && step?.tab && activeTab !== step.tab) setActiveTab(step.tab);
  }, [open, step, activeTab, setActiveTab]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const el = step.tab ? document.querySelector(`[data-tour="tab-${step.tab}"]`) : null;
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    const id = setTimeout(measure, 80);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(id); window.removeEventListener('resize', measure); };
  }, [open, step, activeTab]);

  const finish = () => { localStorage.setItem(key, '1'); setOpen(false); };
  if (!open || !step) return null;
  const last = i === steps.length - 1;
  const cardTop = rect ? rect.top + rect.height + 12 : 96;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="demo-tour">
      <div className="absolute inset-0 bg-slate-900/55" onClick={finish} />
      {rect && (
        <div className="absolute rounded-lg ring-4 ring-amber-400 bg-white/10 pointer-events-none transition-all duration-300" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height, boxShadow: '0 0 0 9999px rgba(15,23,42,0.55)' }} />
      )}
      <div className="absolute left-1/2 -translate-x-1/2 w-[92vw] max-w-md bg-white rounded-xl shadow-2xl border border-amber-200 p-4 transition-all duration-300" style={{ top: Math.min(cardTop, window.innerHeight - 260) }} data-testid="demo-tour-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold" data-testid="demo-tour-progress">{t('tour.step', { n: i + 1, total: steps.length })}</p>
            <h3 className="text-base font-semibold text-slate-900 mt-0.5" data-testid="demo-tour-title">{step.title}</h3>
          </div>
          <button type="button" onClick={finish} className="text-slate-400 hover:text-slate-700" aria-label={t('tour.skip')} data-testid="demo-tour-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid="demo-tour-desc">{step.desc}</p>
        {step.tip && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1.5 mt-2">{step.tip}</p>}
        <div className="flex items-center justify-between mt-4">
          <button type="button" onClick={finish} className="text-xs text-slate-500 hover:text-slate-800 underline" data-testid="demo-tour-skip">{t('tour.skip')}</button>
          <div className="flex gap-2">
            {i > 0 && <Button size="sm" variant="outline" onClick={() => setI(i - 1)} data-testid="demo-tour-prev"><ArrowLeft size={14} className="mr-1" /> {t('tour.prev')}</Button>}
            <Button size="sm" onClick={() => (last ? finish() : setI(i + 1))} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="demo-tour-next">
              {last ? t('tour.finish') : t('tour.next')} {!last && <ArrowRight size={14} className="ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoTour;
